import { assign, createMachine } from 'xstate';
import { OmitStrict } from '@tink/core/src/types';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { SubmitProviderInputsPayload } from '../../../components/screens/molecules/SubmitCredentialsScreen/components/NoFieldsForm/NoFieldsForm';
import { CredentialsRequest } from '../../services/credentialsClient';
import {
  CredentialsService,
  credentialsService,
  SubmitCredentialsPayload,
} from '../../services/credentialsService';
import { CredentialsEntity, CredentialsStatus } from '../../../models/credentials';
import { noop } from '../../../utilities/noop';
import { BANK_LOADING_OVERLAY_DELAY, POLL_INTERVAL } from '../utils/utils';
import { Typegen0 } from './credentialsCreatorMachine.typegen';

export type CredentialsCreatorMachineContext = {
  credentials: CredentialsEntity | undefined;
  enableHandoff: boolean;
  fields: SubmitProviderInputsPayload;
  error?: ApplicationError;
  credentialsError?: string;
  showDefaultFlowLoaders: boolean;
};

export type CredentialsCreatorMachineEvent =
  | {
      type: 'SUBMIT_EVENT';
      fields: SubmitProviderInputsPayload;
    }
  | { type: 'MACHINE_DONE_EVENT'; credentials: CredentialsEntity }
  | { type: 'AUTH_ERROR_EVENT'; credentials: CredentialsEntity }
  | { type: 'DISABLE_HANDOFF_EVENT' };

export type CredentialsCreatorMachineServices = {
  submitCredentials: { data: CredentialsEntity };
  getCredentials: { data: CredentialsEntity };
};

export type RawSubmitCredentialsPayload = OmitStrict<
  SubmitCredentialsPayload,
  'credentialsRequest'
> &
  OmitStrict<CredentialsRequest, 'fields'>;

export type CredentialsCreatorMachineProps = {
  submitCredentialsPayload: RawSubmitCredentialsPayload;
  initialContext: CredentialsCreatorMachineContext;
  shouldAutoSubmit: boolean;
  onCredentialsCreated?: (credentials: CredentialsEntity) => void;
  sendOnAuthErrorAnalytics: VoidFunction;
};

export type CredentialsCreatorMachineTestOverrides = {
  initialState?: Exclude<Typegen0['matchesStates'], object>;
  service?: CredentialsService;
  pollInterval?: number;
  bankLoaderDelay?: number;
};

/*
 * For the initiative of default flows, we included a new type of LoadingOverlays, which should appear after and idle
 * time of three seconds (bankLoaderDelay). For the A/B-Test we added the context variable `showDefaultFlowLoaders`
 * to differentiate between the two flows within the state `SUBMIT_CREDENTIALS_VIEW`
 */

export const createCredentialsCreatorMachine = ({
  initialContext,
  initialState = 'CHECK_INITIAL_PARAMS',
  submitCredentialsPayload,
  shouldAutoSubmit,
  onCredentialsCreated = noop,
  sendOnAuthErrorAnalytics,
  service = credentialsService,
  pollInterval = POLL_INTERVAL,
  bankLoaderDelay = BANK_LOADING_OVERLAY_DELAY,
}: CredentialsCreatorMachineProps & CredentialsCreatorMachineTestOverrides) =>
  createMachine(
    {
      id: 'CredentialsCreatorMachine',
      initial: initialState,
      context: initialContext,
      tsTypes: {} as import('./credentialsCreatorMachine.typegen').Typegen0,
      schema: {
        context: {} as CredentialsCreatorMachineContext,
        events: {} as CredentialsCreatorMachineEvent,
        services: {} as CredentialsCreatorMachineServices,
      },
      predictableActionArguments: true,
      states: {
        CHECK_INITIAL_PARAMS: {
          always: [
            {
              target: 'CREDENTIALS_MACHINE',
              cond: 'check_if_provided_credentials_exist',
            },
            {
              target: 'HANDOFF_CREATOR_MACHINE',
              cond: 'check_if_handoff_enabled',
            },
            {
              target: 'AUTO_SUBMIT_CREDENTIALS',
              cond: 'check_if_auto_submit',
            },
            {
              target: 'SUBMIT_CREDENTIALS_VIEW',
            },
          ],
        },
        CREDENTIALS_MACHINE: {
          on: {
            AUTH_ERROR_EVENT: {
              target: 'SUBMIT_CREDENTIALS_VIEW',
              actions: ['update_credentials_in_context', 'send_authentication_error_analytics'],
            },
          },
        },
        HANDOFF_CREATOR_MACHINE: {
          on: {
            DISABLE_HANDOFF_EVENT: {
              target: 'CHECK_INITIAL_PARAMS',
              actions: 'set_forced_web_flow_in_context',
            },
            MACHINE_DONE_EVENT: {
              target: 'CREDENTIALS_MACHINE',
              actions: 'update_credentials_in_context',
            },
          },
        },
        AUTO_SUBMIT_CREDENTIALS: {
          invoke: {
            src: 'submitCredentials',
            id: 'submitCredentials',
            onDone: [
              {
                target: 'CREDENTIALS_MACHINE',
                actions: ['set_credentials_in_context', 'execute_on_credentials_created'],
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        SUBMIT_CREDENTIALS_VIEW: {
          initial: 'AWAITING',
          states: {
            AWAITING: {
              on: {
                SUBMIT_EVENT: [
                  {
                    target: 'PROCESS_CREDENTIALS',
                    cond: 'check_if_should_show_loaders',
                    actions: 'set_fields_in_context',
                  },
                  {
                    target: 'SUBMIT_CREDENTIALS',
                    actions: 'set_fields_in_context',
                  },
                ],
              },
            },
            PROCESS_CREDENTIALS: {
              type: 'parallel',
              states: {
                UI_LOADER: {
                  initial: 'START_LOADER',
                  states: {
                    START_LOADER: {
                      after: {
                        BANK_LOADER_DELAY: {
                          target: 'SHOW_BANK_LOADER',
                        },
                      },
                    },
                    SHOW_BANK_LOADER: {},
                  },
                },
                CREATE_POLL_CREDENTIALS: {
                  initial: 'CREATE_CREDENTIALS',
                  states: {
                    CREATE_CREDENTIALS: {
                      invoke: {
                        src: 'submitCredentials',
                        id: 'submitCredentialsPoll',
                        onError: [
                          {
                            target: '#CredentialsCreatorMachine.APPLICATION_ERROR',
                            actions: 'set_error_in_context',
                          },
                        ],
                        onDone: [
                          {
                            target: 'GET_CREDENTIALS',
                            actions: [
                              'set_credentials_in_context',
                              'execute_on_credentials_created',
                            ],
                          },
                        ],
                      },
                    },
                    GET_CREDENTIALS: {
                      invoke: {
                        src: 'getCredentials',
                        id: 'getCredentials',
                        onDone: [
                          {
                            target: 'TIMEOUT',
                            actions: 'set_credentials_in_context',
                          },
                        ],
                        onError: [
                          {
                            target: '#CredentialsCreatorMachine.APPLICATION_ERROR',
                            actions: 'set_error_in_context',
                          },
                        ],
                      },
                    },
                    TIMEOUT: {
                      after: {
                        POLL_INTERVAL: {
                          target: 'GET_CREDENTIALS',
                        },
                      },
                      always: {
                        target: '#CredentialsCreatorMachine.SUBMIT_CREDENTIALS_VIEW.DONE',
                        cond: 'credentials_are_in_actionable_state',
                      },
                    },
                  },
                },
              },
            },
            SUBMIT_CREDENTIALS: {
              invoke: {
                src: 'submitCredentials',
                id: 'submitCredentials',
                onError: [
                  {
                    target: '#CredentialsCreatorMachine.APPLICATION_ERROR',
                    actions: 'set_error_in_context',
                  },
                ],
                onDone: [
                  {
                    target: 'DONE',
                    actions: ['set_credentials_in_context', 'execute_on_credentials_created'],
                  },
                ],
              },
            },
            DONE: {
              type: 'final',
            },
          },
          onDone: {
            target: 'CREDENTIALS_MACHINE',
          },
        },
        APPLICATION_ERROR: {
          type: 'final',
        },
      },
    },
    {
      guards: {
        check_if_provided_credentials_exist: ({ credentials }) => !!credentials,
        check_if_handoff_enabled: ({ enableHandoff }) => enableHandoff,
        check_if_auto_submit: () => shouldAutoSubmit,
        credentials_are_in_actionable_state: ({ credentials }) => {
          return (
            credentials?.status !== CredentialsStatus.CREATED &&
            credentials?.status !== CredentialsStatus.AUTHENTICATING
          );
        },
        check_if_should_show_loaders: ({ showDefaultFlowLoaders }) => showDefaultFlowLoaders,
      },
      actions: {
        set_credentials_in_context: assign({ credentials: (_, event) => event.data }),
        update_credentials_in_context: assign({ credentials: (_, event) => event.credentials }),
        set_error_in_context: assign({ error: (_, event) => event.data as ApplicationError }),
        set_forced_web_flow_in_context: assign({ enableHandoff: (_) => false }),
        set_fields_in_context: assign({ fields: (_, event) => event.fields }),
        send_authentication_error_analytics: (_) => {
          sendOnAuthErrorAnalytics();
        },
        execute_on_credentials_created: ({ credentials }) => {
          onCredentialsCreated(credentials!);
        },
      },
      services: {
        submitCredentials: ({ fields, credentials }) => {
          const {
            refreshableItems,
            authenticate,
            providerName,
            appUri,
            productNames,
            triggerRefresh,
            selectedAuthenticationOptions,
            callbackUri,
            userCredentials,
          } = submitCredentialsPayload;

          return service.submitCredentials({
            userCredentials,
            existingCredentials: credentials,
            refreshableItems,
            authenticate,
            credentialsRequest: {
              providerName,
              appUri,
              productNames,
              fields,
              triggerRefresh,
              selectedAuthenticationOptions,
              callbackUri,
            },
          });
        },
        getCredentials: ({ credentials }) => service.getCredentials(credentials!.id),
      },
      delays: {
        POLL_INTERVAL: pollInterval,
        BANK_LOADER_DELAY: bankLoaderDelay,
      },
    }
  );
